import { useCallback, useEffect, useRef, useState } from 'react';

export enum InstallPlatform {
  CHROMIUM = 'chromium',
  IOS = 'ios',
  SAFARI_MAC = 'safari_mac',
  FIREFOX = 'firefox',
  UNKNOWN = 'unknown',
  INSTALLED = 'installed',
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function detectPlatform(): InstallPlatform {
  if (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone
  ) {
    return InstallPlatform.INSTALLED;
  }

  const ua = navigator.userAgent;

  if (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return InstallPlatform.IOS;
  }

  if (/Firefox/i.test(ua) && !/Seamonkey/i.test(ua)) {
    return InstallPlatform.FIREFOX;
  }

  if (/Chrome|Edg|OPR|SamsungBrowser/i.test(ua)) {
    return InstallPlatform.CHROMIUM;
  }

  // macOS Safari (supports "Add to Dock" since macOS Sonoma)
  if (/Safari/i.test(ua) && /Macintosh|MacIntel/i.test(ua)) {
    return InstallPlatform.SAFARI_MAC;
  }

  return InstallPlatform.UNKNOWN;
}

const DISMISS_KEY = 'fire-track-install-dismissed';

export function useInstallPrompt() {
  const [platform, setPlatform] = useState(InstallPlatform.UNKNOWN);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [promptReady, setPromptReady] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1'
  );
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setPromptReady(true);
    };

    const installed = () => {
      setPlatform(InstallPlatform.INSTALLED);
      deferredPromptRef.current = null;
      setPromptReady(false);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const install = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
          setPlatform(InstallPlatform.INSTALLED);
        }
      } catch {
        // prompt() failed (e.g. already consumed) — fall through to guide
        setShowGuide(true);
        return;
      }
      deferredPromptRef.current = null;
      setPromptReady(false);
    } else {
      // No deferred prompt — show manual instructions for any platform
      setShowGuide(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setShowGuide(false);
    localStorage.setItem(DISMISS_KEY, '1');
  }, []);

  const closeGuide = useCallback(() => {
    setShowGuide(false);
  }, []);

  // Always show on supported platforms (not just when beforeinstallprompt fires)
  const canShow =
    !dismissed &&
    platform !== InstallPlatform.INSTALLED &&
    platform !== InstallPlatform.UNKNOWN;

  return {
    platform,
    promptReady,
    canShow,
    install,
    dismiss,
    showGuide,
    closeGuide,
  };
}
